import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCog,
  Phone,
  Mail,
  Star,
  Car,
  MapPin,
  Plus,
  Search,
  Edit2,
  Trash2
} from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import Pagination from '../components/ui/Pagination';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Input from '../components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/Table';
import { userAPI } from '../services/api';
import { formatName } from '../lib/utils';

const Drivers = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, available, busy, offline
  const [page, setPage] = useState(1);
  const PER_PAGE = 12;

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      // Fetch staff members with role 'driver'
      const response = await userAPI.getStaffMembers({ role: 'driver' });
      const driversList = response.data.staffMembers || [];

      // Map the data to include default values for missing fields
      const mappedDrivers = driversList.map(driver => ({
        ...driver,
        status: driver.isActive ? 'available' : 'offline',
        rating: driver.rating || 0,
        totalTrips: driver.totalTrips || 0,
        currentLocation: driver.currentLocation || '-',
        assignedCar: driver.assignedCar || 'Not assigned'
      }));

      setDrivers(mappedDrivers);
      setFilteredDrivers(mappedDrivers);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    filterDrivers();
  }, [searchTerm, statusFilter, drivers]);

  // Reset to the first page whenever the filtered result set changes.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredDrivers.length / PER_PAGE);
  const pagedDrivers = filteredDrivers.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const filterDrivers = () => {
    let filtered = [...drivers];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(driver => driver.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(driver =>
        driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.phone.includes(searchTerm) ||
        driver.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredDrivers(filtered);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { variant: 'success', label: 'Available' },
      busy: { variant: 'warning', label: 'Busy' },
      offline: { variant: 'secondary', label: 'Offline' }
    };

    const config = statusConfig[status] || statusConfig.offline;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getRatingStars = (rating) => {
    return (
      <div className="flex items-center">
        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
        <span className="ml-1 text-sm font-medium text-gray-900">{rating.toFixed(1)}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600">Loading drivers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Drivers Management"
        description="Manage your fleet drivers and assignments"
        action={
          <Button variant="primary" onClick={() => navigate('/staff/new')}>
            <Plus className="h-5 w-5 mr-2" />
            Add Driver
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Drivers</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">{drivers.length}</h3>
              </div>
              <div className="p-4 bg-blue-100 rounded-xl">
                <UserCog className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">
                  {drivers.filter(d => d.status === 'available').length}
                </h3>
              </div>
              <div className="p-4 bg-green-100 rounded-xl">
                <UserCog className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">On Trip</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">
                  {drivers.filter(d => d.status === 'busy').length}
                </h3>
              </div>
              <div className="p-4 bg-yellow-100 rounded-xl">
                <Car className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Rating</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">
                  {(drivers.reduce((sum, d) => sum + d.rating, 0) / drivers.length).toFixed(1)}
                </h3>
              </div>
              <div className="p-4 bg-purple-100 rounded-xl">
                <Star className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Drivers Table */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 items-center mb-4">
            <div className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, phone, or license..."
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 bg-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', activeColor: 'bg-gray-700 text-white' },
                { value: 'available', label: 'Available', color: 'bg-green-50 text-green-700 hover:bg-green-100', activeColor: 'bg-green-500 text-white' },
                { value: 'busy', label: 'Busy', color: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100', activeColor: 'bg-yellow-500 text-white' },
                { value: 'offline', label: 'Offline', color: 'bg-red-50 text-red-700 hover:bg-red-100', activeColor: 'bg-red-500 text-white' },
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setStatusFilter(status.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    statusFilter === status.value ? status.activeColor + ' shadow-sm' : status.color
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">All Drivers ({filteredDrivers.length})</h3>
          {filteredDrivers.length === 0 ? (
            <div className="text-center py-12">
              <UserCog className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No drivers found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Add your first driver to get started'}
              </p>
              <Button variant="primary" onClick={() => navigate('/staff/new')}>
                <Plus className="h-5 w-5 mr-2" />
                Add Driver
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Trips</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Assigned Car</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedDrivers.map((driver) => (
                  <TableRow key={driver._id}>
                    <TableCell>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-3 overflow-hidden">
                          {driver.photo?.url ? (
                            <img
                              src={driver.photo.url}
                              alt={driver.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-semibold">
                              {formatName(driver.name)?.charAt(0) || 'D'}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{formatName(driver.name)}</p>
                          <p className="text-sm text-gray-500">{driver.email || '-'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        {driver.phone || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-mono text-gray-900">
                        {driver.licenseNumber || '-'}
                      </span>
                    </TableCell>
                    <TableCell>{getRatingStars(driver.rating || 0)}</TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-gray-900">
                        {driver.totalTrips || 0}
                      </span>
                    </TableCell>
                    <TableCell>{getStatusBadge(driver.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                        {driver.currentLocation || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-sm text-gray-600">
                        <Car className="h-4 w-4 mr-2 text-gray-400" />
                        {driver.assignedCar || 'Not assigned'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/staff/${driver._id}/edit`)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>
    </div>
  );
};

export default Drivers;
